import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import type { TruckHistory } from "../../types/truckHistory";
import {
  getTruckHistoryApi,
  addTruckHistoryApi,
  deleteTruckHistoryApi,
} from "../../api/truckHistoryApi";
import type { RootState } from "../index";
import type { PaginationResponse } from "../../types/paginationResponse";
import type { Truck } from "../../types/truck";
import { updateTruck } from "./truckSlice";
import { updateTruckInGroup } from "./trucksInGroupSlice";
import { selectCurrentPage, selectPageMetadata } from "./appStateSlice";

interface TruckHistoryState {
  historiesByTruck: Record<string, TruckHistory[]>;
  paginationByTruck: Record<string, PaginationResponse>;
  loadingStates: Record<string, boolean>;
  deletingStates: Record<string, boolean>;
  error: string | null;
}

const initialState: TruckHistoryState = {
  historiesByTruck: {},
  paginationByTruck: {},
  loadingStates: {},
  deletingStates: {},
  error: null,
};

export const fetchTruckHistories = createAsyncThunk<
  { history: TruckHistory[]; pagination: PaginationResponse },
  { truckId: string; page: number; pageSize: number },
  { rejectValue: string }
>(
  "truckHistories/fetchTruckHistories",
  async ({ truckId, page, pageSize }, { rejectWithValue }) => {
    try {
      return await getTruckHistoryApi({ truckId, page, pageSize });
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch truck histories");
    }
  },
);

export const addTruckHistory = createAsyncThunk<
  { history: TruckHistory; truck: Truck },
  {
    truckId: string;
    historyData: {
      location: { cityId: string };
      status: string;
      notes?: string;
      timestamp: string;
    };
  },
  { rejectValue: string }
>(
  "truckHistories/addTruckHistory",
  async ({ truckId, historyData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await addTruckHistoryApi(truckId, historyData);
      const result = {
        history: response.truckHistory,
        truck: response.truck,
      };
      dispatch(updateTruck(response.truck));
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to add truck history");
    }
  },
);

export const deleteTruckHistory = createAsyncThunk<
  { history: TruckHistory; truck: Truck },
  string,
  { rejectValue: string; state: RootState }
>(
  "truckHistories/deleteTruckHistory",
  async (historyId, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await deleteTruckHistoryApi(historyId);
      const result = {
        history: response.truckHistory,
        truck: response.truck,
      };

      // Get current page state
      const state = getState();
      const currentPage = selectCurrentPage(state);
      const pageMetadata = selectPageMetadata(state);

      // Update truck state based on current page
      if (currentPage === "trucks") {
        dispatch(updateTruck(response.truck));
      } else if (currentPage === "truckGroup" && pageMetadata.id) {
        dispatch(updateTruckInGroup({ groupId: pageMetadata.id, truck: response.truck }));
      }

      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete truck history");
    }
  },
);

const truckHistorySlice = createSlice({
  name: "truckHistories",
  initialState,
  reducers: {
    clearTruckHistoriesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTruckHistories.pending, (state, action) => {
        state.loadingStates[action.meta.arg.truckId] = true;
        state.error = null;
      })
      .addCase(fetchTruckHistories.fulfilled, (state, action) => {
        const truckId = action.meta.arg.truckId;
        state.loadingStates[truckId] = false;
        state.historiesByTruck[truckId] = action.payload.history;
        state.paginationByTruck[truckId] = action.payload.pagination;
      })
      .addCase(fetchTruckHistories.rejected, (state, action) => {
        if (action.meta.arg) {
          state.loadingStates[action.meta.arg.truckId] = false;
        }
        state.error = action.payload || "Failed to fetch truck histories";
      })
      .addCase(addTruckHistory.pending, (state) => {
        state.error = null;
      })
      .addCase(addTruckHistory.fulfilled, (state, action) => {
        const truckId = action.payload.history.truckId;
        if (!state.historiesByTruck[truckId]) {
          state.historiesByTruck[truckId] = [];
        }
        state.historiesByTruck[truckId].unshift(action.payload.history);
      })
      .addCase(addTruckHistory.rejected, (state, action) => {
        state.error = action.payload || "Failed to add truck history";
      })
      .addCase(deleteTruckHistory.pending, (state, action) => {
        state.deletingStates[action.meta.arg] = true;
        state.error = null;
      })
      .addCase(deleteTruckHistory.fulfilled, (state, action) => {
        const history = action.payload.history;
        const truckId = history.truckId;

        if (state.historiesByTruck[truckId]) {
          const index = state.historiesByTruck[truckId].findIndex((h) => h._id === history._id);
          if (index !== -1) {
            state.historiesByTruck[truckId][index] = history;
          }
        }

        state.deletingStates[action.meta.arg] = false;
      })
      .addCase(deleteTruckHistory.rejected, (state, action) => {
        if (action.meta.arg) {
          state.deletingStates[action.meta.arg] = false;
        }
        state.error = action.payload || "Failed to delete truck history";
      });
  },
});

// Selectors
export const selectTruckHistories = (state: RootState) => state.truckHistories.historiesByTruck;
export const selectTruckHistoryLoadingStates = (state: RootState) =>
  state.truckHistories.loadingStates;
export const selectTruckHistoryError = (state: RootState) => state.truckHistories.error;
export const selectTruckHistoryPagination = (state: RootState) =>
  state.truckHistories.paginationByTruck;

export const selectTruckHistory = createSelector(
  [selectTruckHistories, (_state: RootState, truckId: string) => truckId],
  (historiesByTruck, truckId) => historiesByTruck[truckId] || [],
);

export const selectTruckHistoryLoading = createSelector(
  [
    (state: RootState) => state.truckHistories.loadingStates,
    (_state: RootState, truckId: string) => truckId,
  ],
  (loadingStates, truckId) => loadingStates[truckId] || false,
);

export const selectTruckHistoryPaginationForTruck = createSelector(
  [
    (state: RootState) => state.truckHistories.paginationByTruck,
    (_state: RootState, truckId: string) => truckId,
  ],
  (paginationByTruck, truckId) => paginationByTruck[truckId],
);

export const selectTruckHistoryDeleting = createSelector(
  [
    (state: RootState) => state.truckHistories.deletingStates,
    (_state: RootState, historyId: string) => historyId,
  ],
  (deletingStates, historyId) => deletingStates[historyId] || false,
);

export const { clearTruckHistoriesError } = truckHistorySlice.actions;
export default truckHistorySlice.reducer;
